import {
  createContext,
  forwardRef,
  useContext,
  useId,
  type HTMLAttributes,
  type LabelHTMLAttributes,
  type ReactNode,
} from 'react';
import * as LabelPrimitive from '@radix-ui/react-label';
import { cn } from '@eon/utils';

/**
 * Field is the generic form-field wrapper used across the admin app. It owns
 * three pieces of state (the input id, the optional error, the optional
 * description) and exposes them to children via context so individual
 * controls can wire `aria-describedby`/`aria-invalid` automatically.
 *
 * Usage:
 *
 *   <Field label="Display name" hint="Shown in lists" required>
 *     <TextField name="displayName" placeholder="Charger 1" />
 *   </Field>
 *
 *   <Field label="OCPP version" error={errors.ocpp}>
 *     <SelectField options={...} />
 *   </Field>
 */

interface FieldContextValue {
  id: string;
  hintId: string;
  errorId: string;
  hasError: boolean;
  hasHint: boolean;
  required: boolean;
}

const FieldContext = createContext<FieldContextValue | null>(null);

/** useField is consumed by control primitives (TextField, SelectField, …). */
export function useField(): FieldContextValue | null {
  return useContext(FieldContext);
}

export interface FieldProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** Visible label rendered above the control. */
  label?: ReactNode;
  /** Optional helper text shown below the control. Hidden when error is set. */
  hint?: ReactNode;
  /** When set, the field is in error state and `error` is rendered instead of `hint`. */
  error?: ReactNode;
  /** Marks the label with a red asterisk. Does NOT enforce HTML required. */
  required?: boolean;
  /** Force a specific id (otherwise auto-generated via useId). */
  id?: string;
  /** Lay out label and control side-by-side instead of stacked. */
  inline?: boolean;
  children: ReactNode;
}

export const Field = forwardRef<HTMLDivElement, FieldProps>(
  ({ label, hint, error, required, id: idProp, inline, className, children, ...props }, ref) => {
    const auto = useId();
    const id = idProp ?? auto;
    const ctx: FieldContextValue = {
      id,
      hintId: `${id}-hint`,
      errorId: `${id}-error`,
      hasError: Boolean(error),
      hasHint: Boolean(hint),
      required: Boolean(required),
    };
    return (
      <FieldContext.Provider value={ctx}>
        <div
          ref={ref}
          className={cn(
            inline ? 'flex flex-row items-center gap-3' : 'flex flex-col gap-1.5',
            className,
          )}
          {...props}
        >
          {label ? (
            <FieldLabel htmlFor={id} className={inline ? 'min-w-32 text-right' : undefined}>
              {label}
              {required ? <span className="text-destructive ml-0.5">*</span> : null}
            </FieldLabel>
          ) : null}
          <div className={inline ? 'flex-1' : 'contents'}>
            {children}
            {error ? (
              <p id={ctx.errorId} className="text-destructive text-xs" role="alert">
                {error}
              </p>
            ) : hint ? (
              <p id={ctx.hintId} className="text-muted-foreground text-xs">
                {hint}
              </p>
            ) : null}
          </div>
        </div>
      </FieldContext.Provider>
    );
  },
);
Field.displayName = 'Field';

/** Plain label primitive — the `Label` re-export uses the same Radix base. */
export const FieldLabel = forwardRef<HTMLLabelElement, LabelHTMLAttributes<HTMLLabelElement>>(
  ({ className, ...props }, ref) => (
    <LabelPrimitive.Root
      ref={ref}
      className={cn(
        'text-foreground text-sm font-medium leading-none',
        'peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
        className,
      )}
      {...props}
    />
  ),
);
FieldLabel.displayName = 'FieldLabel';

/**
 * useFieldA11yProps returns the aria-* attributes a control should spread on
 * its root element when rendered inside a <Field>. Returns an empty object
 * when used outside a Field, so it's safe to call unconditionally.
 */
export function useFieldA11yProps() {
  const ctx = useField();
  if (!ctx) return {};
  const describedBy = [ctx.hasError ? ctx.errorId : null, !ctx.hasError && ctx.hasHint ? ctx.hintId : null]
    .filter(Boolean)
    .join(' ');
  return {
    id: ctx.id,
    'aria-invalid': ctx.hasError ? true : undefined,
    'aria-required': ctx.required || undefined,
    'aria-describedby': describedBy || undefined,
  } as const;
}
